import { useLocation, useNavigate } from 'react-router-dom';
import { options } from 'config/config';

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = options.filters.defaultPageSize;

export interface ListPagination {
  page: number;
  pageSize: number;
}

const parsePositiveInt = (value: string | null, fallback: number): number => {
  const parsed = parseInt(value ?? '', 10);
  return parsed > 0 ? parsed : fallback;
};

export const readPagination = (params: URLSearchParams): ListPagination => ({
  page: parsePositiveInt(params.get('page'), DEFAULT_PAGE),
  pageSize: parsePositiveInt(params.get('page_size'), DEFAULT_PAGE_SIZE),
});

export const writePagination = (
  params: URLSearchParams,
  { page, pageSize }: ListPagination
): URLSearchParams => {
  if (page !== DEFAULT_PAGE) {
    params.set('page', String(page));
  } else {
    params.delete('page');
  }

  if (pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('page_size', String(pageSize));
  } else {
    params.delete('page_size');
  }

  return params;
};

export const buildPath = (
  pathname: string,
  params: URLSearchParams
): string => {
  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
};

export const useListPagination = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pagination = readPagination(new URLSearchParams(location.search));

  const navigateTo = (next: ListPagination) => {
    const params = writePagination(new URLSearchParams(location.search), next);
    navigate(buildPath(location.pathname, params), { replace: true });
  };

  return {
    ...pagination,
    setPage: (page: number) => navigateTo({ ...pagination, page }),
    setPageSize: (pageSize: number) =>
      navigateTo({ page: DEFAULT_PAGE, pageSize }),
  };
};
